// src/pages/Admin/Dashboard.jsx
// ✅ FIXED:
// - getDashboard() response mapped correctly (backend returns { stats, recent_fruits, recent_enquiries })
// - exportData() doesn't exist — removed, replaced with working export
// - recentEnquiries/_id → use enquiry.id (backend uses integer id, not MongoDB _id)
// - Charts handle empty data gracefully

import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent,
  LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Avatar, Tooltip,
} from '@mui/material';
import {
  ShoppingCart, AttachMoney, LocalShipping, People,
  TrendingUp, TrendingDown, Inventory, HourglassEmpty,
  CheckCircle, Refresh, Download, ArrowForward, Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar,
} from 'recharts';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading,        setLoading]       = useState(true);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboard();

      // ✅ Backend returns: { stats:{...}, recent_fruits:[...], recent_enquiries:[...] }
      const raw = response.data;
      setDashboardData({
        // ── Stat counters ──────────────────────────────────────
        totalFruits:          raw.stats?.total_fruits          || 0,
        activeFruits:         raw.stats?.active_fruits         || 0,
        totalUsers:           raw.stats?.total_users           || 0,
        totalBuyers:          raw.stats?.total_buyers          || 0,
        pendingEnquiries:     raw.stats?.pending_enquiries     || 0,
        invoiceSent:          raw.stats?.invoice_sent          || 0,
        pendingPayments:      raw.stats?.pending_payments      || 0,
        pendingVerifications: raw.stats?.pending_verifications || 0,

        // ── Recent data ────────────────────────────────────────
        recentEnquiries: raw.recent_enquiries || [],
        recentFruits:    raw.recent_fruits    || [],

        // ── Chart placeholders (backend doesn't send these yet) ─
        revenueChart:    [],
        orderStatusData: [
          { status: 'Pending',      count: raw.stats?.pending_enquiries  || 0 },
          { status: 'Invoice Sent', count: raw.stats?.invoice_sent       || 0 },
          { status: 'Confirmed',    count: 0 },
          { status: 'Cancelled',    count: 0 },
        ],
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center',
                       alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <LinearProgress sx={{ width: 300, mb: 2 }} />
          <Typography>Loading dashboard data...</Typography>
        </Box>
      </Container>
    );
  }

  const stats = [
    {
      title:  'Total Fruits',
      value:  dashboardData?.totalFruits || 0,
      sub:    `${dashboardData?.activeFruits || 0} active`,
      icon:   <Inventory />,
      color:  '#2196f3',
    },
    {
      title:  'Total Users',
      value:  dashboardData?.totalUsers || 0,
      sub:    `${dashboardData?.totalBuyers || 0} buyers`,
      icon:   <People />,
      color:  '#9c27b0',
    },
    {
      title:  'Pending Enquiries',
      value:  dashboardData?.pendingEnquiries || 0,
      sub:    `${dashboardData?.invoiceSent || 0} invoices sent`,
      icon:   <ShoppingCart />,
      color:  '#ff9800',
    },
    {
      title: 'Pending Actions',
      value: (dashboardData?.pendingPayments || 0) +
             (dashboardData?.pendingVerifications || 0),
      icon:  <HourglassEmpty />,
      color: '#f44336',
      subItems: [
        { label: 'Payments',      value: dashboardData?.pendingPayments      || 0 },
        { label: 'Verifications', value: dashboardData?.pendingVerifications || 0 },
      ],
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Admin Dashboard</Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back! Here's what's happening today.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchDashboardData}>
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2" sx={{ mb: 0.5 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">{stat.value}</Typography>
                    {stat.sub && (
                      <Typography variant="caption" color="text.secondary">{stat.sub}</Typography>
                    )}
                  </Box>
                  <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>
                {stat.subItems && (
                  <Box sx={{ mt: 1 }}>
                    {stat.subItems.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="textSecondary">{item.label}</Typography>
                        <Typography variant="caption" fontWeight="medium">{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Revenue Overview</Typography>
              <Button size="small" endIcon={<ArrowForward />}
                onClick={() => navigate('/admin/payments')}>View Payments</Button>
            </Box>
            {dashboardData?.revenueChart?.length > 0 ? (
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboardData.revenueChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                    <Line type="monotone" dataKey="revenue" stroke="#4caf50"
                      strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 280, display: 'flex', alignItems: 'center',
                         justifyContent: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <AttachMoney sx={{ fontSize: 60, color: 'text.disabled' }} />
                  <Typography color="text.secondary">
                    Revenue data will appear here as payments are processed
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Enquiry Status</Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dashboardData?.orderStatusData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#2196f3" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Data Tables */}
      <Grid container spacing={3}>

        {/* Recent Enquiries */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Recent Enquiries</Typography>
              <Button size="small" endIcon={<ArrowForward />}
                onClick={() => navigate('/admin/enquiries')}>View All</Button>
            </Box>
            {dashboardData?.recentEnquiries?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Buyer</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.recentEnquiries.map((enquiry) => (
                      // ✅ Use enquiry.id (integer) not enquiry._id (MongoDB)
                      <TableRow key={enquiry.id || enquiry._id} hover
                        onClick={() => navigate(`/admin/enquiries/${enquiry.id || enquiry._id}`)}
                        sx={{ cursor: 'pointer' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            #{enquiry.enquiryId || enquiry.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                              {(enquiry.buyerName || 'B').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                              {enquiry.buyerName || '—'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 80 }}>
                            {enquiry.product || enquiry.productName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={enquiry.status || 'pending'} size="small"
                            color={getStatusColor(enquiry.status)}
                            sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <ShoppingCart sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No enquiries yet</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Fruits */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Recent Fruits</Typography>
              <Button size="small" endIcon={<ArrowForward />}
                onClick={() => navigate('/admin/fruits')}>View All</Button>
            </Box>
            {dashboardData?.recentFruits?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fruit</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.recentFruits.map((fruit) => (
                      <TableRow key={fruit.id} hover
                        onClick={() => navigate(`/admin/fruits/edit/${fruit.id}`)}
                        sx={{ cursor: 'pointer' }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              src={fruit.image_path
                                ? `${(process.env.REACT_APP_BACKEND_URL||process.env.REACT_APP_API_URL?.replace('/api','')||'http://localhost:5000').replace(/\/$/,'')}/static/uploads/fruits/${fruit.image_path}`
                                : undefined}
                              variant="rounded"
                              sx={{ width: 32, height: 32 }}>
                              {(fruit.name || 'F').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight="medium">
                              {fruit.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{fruit.category}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            ${parseFloat(fruit.price_per_unit || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={fruit.active ? 'Active' : 'Inactive'} size="small"
                            color={fruit.active ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No fruits added yet</Typography>
                <Button variant="outlined" size="small" sx={{ mt: 1 }}
                  onClick={() => navigate('/admin/fruits/add')}>
                  Add First Fruit
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* System Status */}
      <Paper sx={{ p: 3, mt: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>System Status</Typography>
        <Grid container spacing={2}>
          {[
            { label: 'API Status',  value: 'Operational', color: '#4caf50', icon: <CheckCircle /> },
            { label: 'Database',    value: 'Connected',   color: '#4caf50', icon: <CheckCircle /> },
            { label: 'Storage',     value: 'Available',   color: '#4caf50', icon: <CheckCircle /> },
            { label: 'Uptime',      value: '99.9%',       color: '#4caf50', icon: <CheckCircle /> },
          ].map((item) => (
            <Grid item xs={6} md={3} key={item.label}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: item.color, width: 36, height: 36 }}>
                  {item.icon}
                </Avatar>
                <Box>
                  <Typography variant="body2" color="textSecondary">{item.label}</Typography>
                  <Typography variant="body1" fontWeight="medium">{item.value}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
};

export default AdminDashboard;